import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Group, Tabs } from '@mantine/core';
import { IconPinFilled } from '@tabler/icons-react';

import { useGetPinnedNotesQuery } from '@/store/slices/api/clinicalNotesApiSlice';

import EnrollmentGapIndicator from '@/pages/physician/PatientOverviewPage/EnrollmentGapIndicator';
import StickyNoteTab from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/StickyNoteTab';

import { PatientTabType } from '../types';

import CareTeamDetails from './CareTeamDetails';
import MedicalInfoDetails from './MedicalInfoDetails';
import SettingsSection from './SettingsSection';
import styles from './TabsSection.module.scss';

import type { ChecklistSectionKey } from '@/utils/enrollmentChecklist';

interface TabConfig {
  value: PatientTabType;
  label: string;
  icon?: React.ReactNode;
  component: React.ComponentType<{ patientId: string }>;
  /** Enrollment checklist section whose gaps show on this tab. */
  gapSection?: ChecklistSectionKey;
}

interface TabsSectionProps {
  patientId: string;
}

const TabsSection = ({ patientId }: TabsSectionProps) => {
  const [selectedTab, setSelectedTab] = useState<PatientTabType>(PatientTabType.CareTeam);

  const { data: pinnedNotes } = useGetPinnedNotesQuery({ patientId }, { skip: !patientId });
  const hasSticky = !!pinnedNotes?.pinnedNotes?.some((note) => note.isSticky && !note.isArchived);
  const didDefaultToSticky = useRef(false);

  useEffect(() => {
    if (hasSticky && !didDefaultToSticky.current) {
      didDefaultToSticky.current = true;
      setSelectedTab(PatientTabType.StickyNote);
    }
  }, [hasSticky]);

  const tabConfigs = useMemo<TabConfig[]>(
    () => [
      ...(hasSticky
        ? [
            {
              value: PatientTabType.StickyNote,
              label: 'Sticky Note',
              icon: <IconPinFilled size={13} />,
              component: StickyNoteTab
            }
          ]
        : []),
      {
        value: PatientTabType.CareTeam,
        label: 'Care Team',
        component: CareTeamDetails,
        gapSection: 'clinic'
      },
      {
        value: PatientTabType.PatientSettings,
        label: 'Patient Settings',
        component: SettingsSection
      },
      {
        value: PatientTabType.MedicalInfo,
        label: 'Medical Info',
        component: MedicalInfoDetails,
        gapSection: 'clinical'
      }
    ],
    [hasSticky]
  );

  const handleTabChange = useCallback((value: string | null) => {
    if (value) {
      setSelectedTab(value as PatientTabType);
    }
  }, []);

  const renderTab = useCallback(
    ({ value, label, icon, gapSection }: TabConfig) => (
      <Tabs.Tab
        key={value}
        value={value}
        className={`${styles.tab} ${value === PatientTabType.StickyNote ? styles.stickyTab : ''}`}>
        <Group gap={5} align="center" wrap="nowrap">
          {icon}
          {label}
          {gapSection && <EnrollmentGapIndicator section={gapSection} />}
        </Group>
      </Tabs.Tab>
    ),
    []
  );

  const renderTabPanel = useCallback(
    ({ value, component: Component }: TabConfig) => (
      <Tabs.Panel key={value} value={value}>
        <div
          className={`${styles.content} ${
            value === PatientTabType.StickyNote ? styles.contentSticky : ''
          }`}>
          <Component patientId={patientId} />
        </div>
      </Tabs.Panel>
    ),
    [patientId]
  );

  return (
    <div className={styles.tabsSection}>
      <Tabs variant="none" value={selectedTab} onChange={handleTabChange}>
        <Tabs.List>{tabConfigs.map(renderTab)}</Tabs.List>
        {tabConfigs.map(renderTabPanel)}
      </Tabs>
    </div>
  );
};

export default TabsSection;
